<script setup>
  import {computed, inject, ref} from "vue";

  import DeveloperContainer from "@components/_DevOnly/DeveloperContainer.vue";
  import ComponentIconAiSparkle from "@components/Icons/IconAiSparkle.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {usePage} from "@inertiajs/vue3";
  import {
    MedButton,
    MedCheckbox,
    MedFormField,
    MedInfoButton,
    MedLabel,
    MedTag,
    MedTooltip,
  } from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  import ComponentAiPrioritizationDialog from "./AiPrioritizationDialog.vue";

  defineOptions({name: "PagesRecordResultListFilter"});

  defineProps({
    record: {
      type: Object,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    checks: {
      type: Array,
      default: null,
    },
  });

  const pageProperties = usePage().props;

  const open = ref(true);

  const resultConfig = inject("resultConfig");
  const sumCommunicationDoctor = inject("sumCommunicationDoctor");
  const sumCommunicationPatient = inject("sumCommunicationPatient");
  const resetDefaultResultConfig = inject("resetDefaultResultConfig");

  const refAiPrioritizationDialog = ref(null);

  const justGeneratedPrioritization = ref(false);
  const hasPrioritization = computed(
    () => justGeneratedPrioritization.value || pageProperties.hasAiPrioritizationResult === true,
  );

  // number of check hits currently marked by the AI prioritization
  const aiPrioritizedCount = computed(() => resultConfig.value.aiPrioritizedClaims?.length ?? 0);

  // reason the AI prioritization feature is locked
  const aiPermissionLockReason = computed(() => {
    if (!pageProperties.can?.allow_ai_prioritization) {
      return "Diese KI-Funktion steht Ihnen im MediCheck Pro zur Verfügung.";
    }
    if (!pageProperties.can?.activate_ai_features) {
      return "Um die KI-Priorisierung zu nutzen, schalten Sie bitte die KI-Funktionen in Ihren Einstellungen frei.";
    }
    return null;
  });

  const aiPrioritizationDisabled = computed(() => aiPermissionLockReason.value !== null);
  const aiPrioritizationTooltip = computed(() => aiPermissionLockReason.value);

  const aiFilterDisabled = computed(() => {
    // always allow disabling the filter if checked
    if (resultConfig.value.showAiPrioritizedClaims) {
      return false;
    }

    return aiPermissionLockReason.value !== null || !resultConfig.value.aiPrioritizedClaims?.length;
  });

  const aiFilterTooltip = computed(() => {
    // no explanation needed while the filter is actually selectable
    if (!aiFilterDisabled.value) {
      return null;
    }

    return aiPermissionLockReason.value ?? "Bitte zuerst KI-Priorisierung erstellen.";
  });

  function openAiPrioritization() {
    refAiPrioritizationDialog.value.open(hasPrioritization.value ? "result" : "hint");
  }

  function toggleFilter() {
    open.value = !open.value;
  }

  function resetDefaults(value) {
    if (value === true) {
      resetDefaultResultConfig();
    }
  }
</script>

<template>
  <div class="sticky top-24 z-40 print:hidden" :data-test="getTestId('result-filter')">
    <div
      class="absolute -right-16 z-20 -mr-4 ml-auto w-fit max-w-sm border-x-1 border-gray-100 bg-white px-2 py-4 shadow-lg"
    >
      <div :class="{closedCard: !open}">
        <div :class="cn('flex items-center', {'space-x-2': open})">
          <MedButton
            :aria-label="open ? 'Filtermenü ausblenden' : 'Filtermenü einblenden'"
            :aria-expanded="open"
            flat
            :data-test="getTestId('result-filter-toggle-button')"
            icon="chevron_left"
            size="sm"
            rounded="full"
            :icon-class="cn('transition-transform duration-200', {'rotate-180': open})"
            @click="toggleFilter()"
          />

          <span v-show="open" class="text-xl font-semibold">Ergebnisse filtern</span>
        </div>

        <div v-if="$page.props.client.license.is_pro && open" class="mt-2 mb-6 flex justify-between pl-3">
          <ul v-show="open" class="w-64">
            <li class="items-start">
              <ComponentSelect
                v-model="resultConfig.analysistype"
                class="w-full max-w-sm"
                :test-id="getTestId('result-filter-analysis-type')"
              >
                <option value="1">Typ 1 (nur Medikation)</option>
                <option value="2a">Typ 2a (Medikation u. Patientengespräch)</option>
                <option value="2b">Typ 2b (Medikation u. Indikationen / Laborwerte)</option>
                <option value="3">Typ 3 (alles)</option>
              </ComponentSelect>
            </li>
          </ul>

          <MedInfoButton
            size="lg"
            side="top"
            class="pt-2"
            aria-label="Analysetyp Info"
            :data-test="getTestId('filter-analyse-type-info-button')"
          >
            <p class="mb-2 text-sm">
              Hier können Sie den Typ der Analyse wählen, der auf Basis der eingegebenen Parameter die
              Datenbankrecherche beeinflusst.
            </p>
            <dl class="max-w-sm space-y-1 text-sm">
              <div>
                <dt class="font-bold">Typ 1</dt>
                <dd>Nur Medikation (inkl. Dosierung)</dd>
              </div>
              <div>
                <dt class="font-bold">Typ 2a</dt>
                <dd>Medikation inkl. Dosierung + Patienten-Information (z.B. „Brown-Bag Gespräch")</dd>
              </div>
              <div>
                <dt class="font-bold">Typ 2b</dt>
                <dd>Medikation inkl. Dosierung und klinische Daten (Laborwerte / Erkrankungen)</dd>
              </div>
              <div>
                <dt class="font-bold">Typ 3</dt>
                <dd>Alles</dd>
              </div>
            </dl>
          </MedInfoButton>
        </div>

        <div>
          <ul :class="cn('mt-4 space-y-3', {'ml-2': open, 'flex flex-col items-center': !open})">
            <li>
              <MedTooltip content="Risiken: sehr hoch" side="left" :disabled="open">
                <MedFormField
                  :orientation="open ? 'horizontal' : 'vertical'"
                  :disabled="resultConfig.showCommunicationDoc || resultConfig.showCommunicationPatient"
                >
                  <MedCheckbox
                    v-model="resultConfig.veryhighrisk"
                    colored-border
                    variant="riskVeryHigh"
                    :data-test="getTestId('result-filter-very-high-risk-checkbox')"
                  />

                  <MedLabel v-if="open">
                    Risiken: sehr hoch
                    <span class="text-risk-very-high">(rot)</span>
                  </MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>

            <li>
              <MedTooltip content="Risiken: mittel - hoch" side="left" :disabled="open">
                <MedFormField
                  :orientation="open ? 'horizontal' : 'vertical'"
                  :disabled="resultConfig.showCommunicationDoc || resultConfig.showCommunicationPatient"
                >
                  <MedCheckbox
                    v-model="resultConfig.highrisk"
                    colored-border
                    variant="riskHigh"
                    :data-test="getTestId('result-filter-high-risk-checkbox')"
                  />

                  <MedLabel v-if="open">
                    Risiken: mittel &ndash; hoch
                    <span class="text-risk-high">(orange)</span>
                  </MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>

            <li>
              <MedTooltip content="Risiken: gering - mittel" side="left" :disabled="open">
                <MedFormField
                  :orientation="open ? 'horizontal' : 'vertical'"
                  :disabled="resultConfig.showCommunicationDoc || resultConfig.showCommunicationPatient"
                >
                  <MedCheckbox
                    v-model="resultConfig.midrisk"
                    colored-border
                    variant="riskMedium"
                    :data-test="getTestId('result-filter-mid-risk-checkbox')"
                  />

                  <MedLabel v-if="open">
                    Risiken: gering &ndash; mittel
                    <span class="text-risk-medium">(gelb)</span>
                  </MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>

            <li>
              <MedTooltip content="Risiken: sonstige" side="left" :disabled="open">
                <MedFormField
                  :orientation="open ? 'horizontal' : 'vertical'"
                  :disabled="resultConfig.showCommunicationDoc || resultConfig.showCommunicationPatient"
                >
                  <MedCheckbox
                    v-model="resultConfig.lowrisk"
                    colored-border
                    variant="riskLow"
                    :data-test="getTestId('result-filter-low-risk-checkbox')"
                  />

                  <MedLabel v-if="open">
                    Risiken: sonstige
                    <span class="text-risk-low">(grau)</span>
                  </MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>

            <li>
              <MedTooltip content="Adhärenz und Prävention" side="left" :disabled="open">
                <MedFormField
                  :orientation="open ? 'horizontal' : 'vertical'"
                  :disabled="resultConfig.showCommunicationDoc || resultConfig.showCommunicationPatient"
                >
                  <MedCheckbox
                    v-model="resultConfig.showNoncomplianceAndPrevention"
                    colored-border
                    :data-test="getTestId('result-filter-show-non-compliance-and-prevention-checkbox')"
                  />

                  <MedLabel v-if="open">Adhärenz und Prävention</MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>

            <li v-show="!open" class="mt-4">
              <!--
                workaround ".capture", obsolete after 4.10.x: MedTooltip renders its trigger with as-child, so the tooltip's own
                click handler and ours end up in one array. MedButton (@pharma4u/medicheck-components 0.18.3) binds
                $attrs explicitly and inherits them on top, and Vue's mergeProps only de-duplicates identical function
                references - never arrays - so that array is appended a second time and toggleFilter() runs twice per
                click. The capture modifier keeps our handler out of the merged array. Drop it as soon as MedButton
                ships with inheritAttrs: false.
              -->
              <MedTooltip content="weitere Filter" side="left">
                <MedButton
                  aria-label="Weitere Filter anzeigen"
                  flat
                  icon="more_horiz"
                  size="sm"
                  rounded="full"
                  :data-test="getTestId('result-filter-more-filters-button')"
                  @click.capture="toggleFilter()"
                />
              </MedTooltip>
            </li>
          </ul>

          <ul
            v-if="pageProperties.can?.allow_ai_prioritization"
            v-show="open"
            class="mt-4 ml-2 space-y-3 border-t-2 border-gray-100 pt-4"
          >
            <li>
              <MedTooltip :disabled="aiFilterTooltip === null" :content="aiFilterTooltip">
                <MedFormField orientation="horizontal" :disabled="aiFilterDisabled">
                  <MedCheckbox
                    v-model="resultConfig.showAiPrioritizedClaims"
                    :data-test="getTestId('result-filter-show-prioritized-doctor-claims-checkbox')"
                  />

                  <MedLabel>
                    KI-Vorschlag (ausgewählt: {{ aiPrioritizedCount }})
                    <ComponentIconAiSparkle class="inline h-4" />
                  </MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>

            <li>
              <MedTooltip :disabled="aiPrioritizationTooltip === null" :content="aiPrioritizationTooltip">
                <MedButton
                  :disabled="aiPrioritizationDisabled"
                  :data-test="getTestId('ai-prioritization-button')"
                  @click="openAiPrioritization()"
                >
                  <ComponentIconAiSparkle class="h-4 w-auto" />
                  {{ hasPrioritization ? "KI-Priorisierung öffnen" : "KI-Priorisierung erstellen" }}
                  <MedTag label="BETA" variant="blue" />
                </MedButton>
              </MedTooltip>
            </li>
          </ul>

          <ul v-show="open" class="mt-4 ml-2 space-y-3 border-t-2 border-gray-100 pt-4">
            <li>
              <MedFormField orientation="horizontal" :disabled="sumCommunicationDoctor === 0">
                <MedCheckbox
                  v-model="resultConfig.showCommunicationDoc"
                  :data-test="getTestId('result-filter-show-communication-doc-checkbox')"
                />

                <MedLabel>an Arzt (ausgewählt: {{ sumCommunicationDoctor }})</MedLabel>
              </MedFormField>
            </li>

            <li>
              <MedFormField orientation="horizontal" :disabled="sumCommunicationPatient === 0">
                <MedCheckbox
                  v-model="resultConfig.showCommunicationPatient"
                  :data-test="getTestId('result-filter-show-communication-patient-checkbox')"
                />

                <MedLabel>an Patienten (ausgewählt: {{ sumCommunicationPatient }})</MedLabel>
              </MedFormField>
            </li>
          </ul>

          <DeveloperContainer>
            <ul v-show="open" class="mt-4 ml-2 space-y-3 border-t-2 border-gray-100 pt-4">
              <li>
                <MedButton
                  variant="dev"
                  as="a"
                  target="_blank"
                  :href="$route('ai-debugger.index') + '?id=' + record.id + '&feature=prioritization'"
                  :data-test="getTestId('ai-debug-prioritization-button')"
                >
                  AI Debugger (prioritization)
                </MedButton>
              </li>

              <li>
                <MedButton
                  variant="dev"
                  as="a"
                  target="_blank"
                  :href="$route('ai-debugger.index') + '?id=' + record.id + '&feature=test_chat_bot'"
                  :data-test="getTestId('ai-debug-chatbot-button')"
                >
                  AI Debugger (test_chat_bot)
                </MedButton>
              </li>
            </ul>
          </DeveloperContainer>

          <ul v-show="open" class="mt-4 ml-2 space-y-3 border-t-2 border-gray-100 pt-4">
            <li>
              <MedTooltip
                :content="
                  resultConfig.defaultSetting ? 'Einstellung entspricht dem Standard' : 'Auf Standard zurücksetzen'
                "
                side="left"
              >
                <MedFormField orientation="horizontal" :disabled="resultConfig.defaultSetting">
                  <MedCheckbox
                    v-model="resultConfig.defaultSetting"
                    :data-test="getTestId('result-filter-default-setting-checkbox')"
                    @change="(value) => resetDefaults(value)"
                  />

                  <MedLabel>Standard</MedLabel>
                </MedFormField>
              </MedTooltip>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <ComponentAiPrioritizationDialog
    v-if="pageProperties.can?.allow_ai_prioritization"
    ref="refAiPrioritizationDialog"
    :patient="patient"
    :record="record"
    :checks="checks"
    @generated="justGeneratedPrioritization = true"
  />
</template>
